import { useEffect, useRef, useState } from "react";

type UseInViewOptions = {
  /** Fraction of the element that must be visible to count as "in view". */
  threshold?: number;
  /** CSS-style margin to grow/shrink the trigger box. */
  rootMargin?: string;
  /** When true (default), the hook disconnects after the first hit so the
   *  entrance animation only plays once. */
  once?: boolean;
};

/**
 * Tiny scroll-trigger hook used across Design B for entrance choreography.
 * Returns a ref to attach to a wrapper element and a boolean that flips
 * to `true` the first time the element crosses the viewport.
 *
 * Falls back to `inView = true` immediately when IntersectionObserver is
 * unavailable so SSR / older browsers still render content.
 */
export function useInView<T extends HTMLElement>(
  { threshold = 0.12, rootMargin = "0px 0px -60px 0px", once = true }: UseInViewOptions = {},
) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, inView };
}

export default useInView;
